import React, { useCallback, useState } from 'react';
import { List, Button, Typography, Divider, Space } from 'antd';
import { MailOutlined, SendOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { estimateApi } from '../../../services/api';
import { Estimate } from '../../../types';
import { usePanelData } from './usePanelData';
import PanelCard from './PanelCard';
import SendEstimateModal from '../../Estimate/SendEstimateModal';

const { Text } = Typography;

const money = (n?: number) =>
  `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const daysSince = (iso?: string) => (iso ? dayjs().diff(dayjs(iso), 'day') : null);
const customerLabel = (e: Estimate) =>
  e.customer ? `${e.customer.first_name} ${e.customer.last_name}` : '';
const vehicleLabel = (e: Estimate) =>
  e.vehicle ? `${e.vehicle.year} ${e.vehicle.make} ${e.vehicle.model}` : '';

interface Lists { sent: Estimate[]; drafts: Estimate[]; }

const EstimatesActionPanel: React.FC = () => {
  const navigate = useNavigate();
  const [sending, setSending] = useState<Estimate | null>(null);

  const fetcher = useCallback(async (): Promise<Lists> => {
    const [sent, drafts] = await Promise.all([
      estimateApi.getAll({ status: 'sent', limit: 5, sort: 'sent_at:asc' }).then((r) => r.data),
      estimateApi.getAll({ status: 'draft', limit: 5 }).then((r) => r.data),
    ]);
    return { sent, drafts };
  }, []);

  const { data, loading, error, reload } = usePanelData<Lists>(fetcher);
  const sent = data?.sent ?? [];
  const drafts = data?.drafts ?? [];

  return (
    <>
      <PanelCard
        title="Estimates — Waiting on Customer"
        icon={<MailOutlined style={{ color: '#1890ff' }} />}
        count={sent.length + drafts.length}
        loading={loading}
        error={error}
        isEmpty={sent.length === 0 && drafts.length === 0}
        emptyText="No estimates waiting on customers"
        onRetry={reload}
        extra={<Button type="link" onClick={() => navigate('/owner/estimates')}>View all</Button>}
      >
        {sent.length > 0 && (
          <List
            dataSource={sent}
            renderItem={(e) => (
              <List.Item
                actions={[
                  <Button key="resend" size="small" icon={<MailOutlined />} onClick={() => setSending(e)}>
                    Resend
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={<Text strong>{e.estimate_number}</Text>}
                  description={
                    <span>
                      {customerLabel(e)} · {vehicleLabel(e)} — {money(e.total_amount)}
                      {daysSince(e.sent_at) !== null && (
                        <Text type="secondary"> · Sent {daysSince(e.sent_at)}d ago</Text>
                      )}
                    </span>
                  }
                />
              </List.Item>
            )}
          />
        )}

        {drafts.length > 0 && (
          <>
            <Divider style={{ margin: '8px 0' }} />
            <Text type="secondary">{drafts.length} draft(s) not yet sent</Text>
            <List
              dataSource={drafts}
              renderItem={(e) => (
                <List.Item
                  actions={[
                    <Button key="send" size="small" type="primary" icon={<SendOutlined />} onClick={() => setSending(e)}>
                      Send
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={<Text>{e.estimate_number}</Text>}
                    description={<Space size={4}>{customerLabel(e)} · {money(e.total_amount)}</Space>}
                  />
                </List.Item>
              )}
            />
          </>
        )}
      </PanelCard>

      {sending && (
        <SendEstimateModal
          visible={!!sending}
          estimateId={sending.id!}
          estimateNumber={sending.estimate_number}
          customerEmail={sending.customer?.email}
          customerPhone={sending.customer?.phone}
          // G2: the checkpoint needs the records to compare against the policy.
          // The list endpoint carries the full customer but no vehicle, and the
          // hook stays silent about any field it has no column for — so vehicle
          // requirements simply are not judged from this panel rather than being
          // reported as missing.
          customerId={sending.customer?.id ?? sending.customer_id ?? null}
          customer={sending.customer ?? null}
          vehicle={sending.vehicle ?? null}
          onClose={() => setSending(null)}
          onSent={() => {
            setSending(null);
            reload();
          }}
        />
      )}
    </>
  );
};

export default EstimatesActionPanel;
